import { useEffect } from "react";

// Consumer-side workaround for the @loffa LGrid scroll-jump. Clicking any cell fires the grid's
// internal onRowClick -> setSelectedDataItem (see UIModules Grid.js), which re-renders the grid,
// re-maps every row, and resets the scroll of whatever container it lives in. That handler can't
// be overridden from the consumer (LGrid sets onRowClick after spreading props), so instead we
// snapshot the scroll position of every scrollable container around the grid on pointer-down and
// restore it right after the grid has re-rendered.
//
// Pass a ref attached to an element that wraps (or is inside) the LGrid.
export function useGridScrollGuard(wrapRef) {
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const onPointerDown = () => {
      // window + every scrollable ancestor + the grid's own scroll body
      const snaps = [{ el: null, top: window.scrollY }];
      for (let node = wrap; node; node = node.parentElement) {
        if (node.scrollHeight > node.clientHeight) {
          const oy = getComputedStyle(node).overflowY;
          if (oy === "auto" || oy === "scroll") snaps.push({ el: node, top: node.scrollTop });
        }
      }
      const content = wrap.querySelector(".k-grid-content");
      if (content) snaps.push({ el: content, top: content.scrollTop });

      const restore = () => {
        for (const s of snaps) {
          if (s.el === null) {
            if (window.scrollY !== s.top) window.scrollTo(0, s.top);
          } else if (s.el.scrollTop !== s.top) {
            s.el.scrollTop = s.top;
          }
        }
      };
      // Restore before the next paint (undoes the jump invisibly), plus once more as a backstop
      // for browsers that scroll after paint.
      requestAnimationFrame(restore);
      setTimeout(restore, 0);
    };

    wrap.addEventListener("pointerdown", onPointerDown, true);
    return () => wrap.removeEventListener("pointerdown", onPointerDown, true);
  }, [wrapRef]);
}
